import apiInstance from '@api';
import { CreateChangeRequestPayload } from '@api/support/SupportAPI';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { FileInputValue } from '../../../FileUploader/types';
import FormFieldsContainer from '../../../Form/components/FormFieldContainer';
import SchemaRenderer from '../../../Form/components/SchemaRenderer';
import toastMessage from '../../../Toast';

import {
  MAX_ATTACHMENT_SIZE,
  NO_ERROR_MESSAGE,
  TASK_DEFAULT_VALUES,
} from '../../constants';
import { StyledFileUploader } from '../../styles';
import { TaskRequestFormProps, TaskRequestFormValues } from '../../types';
import { buildTaskRequestFormSchema } from '../../utils';

import FormActions from '../FormActions';

// Bug / change request / new feature / data issue / question: uploads any
// attachments first, then raises a Teamwork task and links the files to it.
const TaskRequestForm: FC<TaskRequestFormProps> = ({
  requestType,
  onCancel,
  onSubmitted,
}) => {
  const [files, setFiles] = useState<FileInputValue[]>([]);
  const [filesError, setFilesError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const schema = useMemo(() => buildTaskRequestFormSchema(), []);
  const methods = useForm<TaskRequestFormValues>({
    mode: 'onSubmit',
    defaultValues: TASK_DEFAULT_VALUES,
  });

  // Uploads one file to the Teamwork S3 bucket and returns the pending-file ref
  // that the task is attached to once it exists.
  const uploadFile = useCallback(async (file: File) => {
    const { data: presignedResponse } =
      await apiInstance.support.getUploadPresignedUrl({
        file_name: file.name,
        file_size: file.size,
      });
    const { ref, url } = presignedResponse?.data ?? {};

    if (!ref || !url) {
      throw new Error(`Could not get an upload URL for ${file.name}`);
    }

    await apiInstance.support.uploadFileToPresignedUrl(url, file);

    return ref as string;
  }, []);

  const handleSubmit = useCallback(
    async (values: TaskRequestFormValues) => {
      setSubmitting(true);

      const payload: CreateChangeRequestPayload = {
        page_url: window.location.href,
        request_type: requestType,
        report_title: values.report_title.trim(),
        report_description: values.report_description.trim(),
        error_message: values.error_message.trim() || NO_ERROR_MESSAGE,
        additional_context: values.additional_context.trim(),
        user_details: {
          userAgent: window.navigator.userAgent,
          referrer: document.referrer,
        },
      };

      const attachments = files
        .map((file) => file.data)
        .filter((file): file is File => Boolean(file));

      try {
        // Files are uploaded before the task is created, so a failed upload
        // leaves no orphan task behind - the user just retries.
        const refs: string[] = [];
        for (const file of attachments) {
          refs.push(await uploadFile(file));
        }

        const { data: response } =
          await apiInstance.support.createChangeRequest(payload);
        const taskId = Number(response?.data?.task_id);

        if (refs.length && taskId) {
          try {
            for (const ref of refs) {
              await apiInstance.support.attachFileToTask({
                task_id: taskId,
                ref_id: ref,
              });
            }
          } catch (error) {
            console.error('Failed to attach a file to the request:', error);
            toastMessage.warning(
              'Your request was submitted, but the attachments could not be uploaded.',
            );
            onSubmitted();
            return;
          }
        }

        toastMessage.success('Your request has been submitted. Thank you!');
        onSubmitted();
      } catch (error) {
        console.error('Failed to submit the change request:', error);
        toastMessage.error(
          'Failed to submit your request. Please try again later.',
        );
      } finally {
        setSubmitting(false);
      }
    },
    [files, onSubmitted, requestType, uploadFile],
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <FormFieldsContainer columns={1}>
          <SchemaRenderer schema={schema} />
        </FormFieldsContainer>
        <StyledFileUploader
          label="Attachments"
          name="change_request_attachments"
          multi
          maxSize={MAX_ATTACHMENT_SIZE}
          description="Screenshots or files that help us understand the request."
          error={filesError}
          setError={setFilesError}
          clearError={() => setFilesError(undefined)}
          hideSubmitButton
          value={files}
          onChange={setFiles}
        />
        <FormActions submitting={submitting} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
};

export default TaskRequestForm;
